import React from "react";
import PropTypes from "prop-types";
import classNames from "classnames";

import { getProgressClass } from "lib/util";

const ProgressCirclePart = ({
    innerWidth,
    outerWidth,
    strokeWidth,
    percent,
    className,
    gapWidth,
    stroke,
}) => {
    if (percent === 0) {
        return null;
    }

    const cx = outerWidth / 2;
    const cy = outerWidth / 2;

    const r = innerWidth / 2 - strokeWidth / 2;
    const array = 2 * Math.PI * r;

    const start = array * gapWidth;
    const offset = (array - start) * (1 - percent / 100) + start;

    return (
        <circle
            className={className}
            cx={cx}
            cy={cy}
            r={r}
            strokeWidth={strokeWidth}
            strokeDasharray={array}
            strokeDashoffset={offset}
            stroke={stroke}
        />
    );
};

ProgressCirclePart.propTypes = {
    innerWidth: PropTypes.number.isRequired,
    outerWidth: PropTypes.number.isRequired,
    percent: PropTypes.number.isRequired,
    strokeWidth: PropTypes.number.isRequired,
    className: PropTypes.string,
    gapWidth: PropTypes.number.isRequired,
    stroke: PropTypes.string,
};

ProgressCirclePart.defaultProps = {
    className: null,
    stroke: null,
};

// See also: https://codepen.io/xgad/post/svg-radial-progress-meters
const ProgressCircle = ({ percentProgress, strokeWidth, width, gapWidth, className, children }) => {
    const svgClasses = classNames("progress-circle", getProgressClass(percentProgress), className);
    const halfStroke = Math.round(strokeWidth / 3);
    const innerWidth = width - halfStroke * 2;

    return (
        <div className={svgClasses}>
            <svg className="progress-circle__svg" width={width} height={width}>
                <ProgressCirclePart
                    innerWidth={innerWidth}
                    outerWidth={width}
                    strokeWidth={halfStroke}
                    percent={100}
                    gapWidth={gapWidth}
                    className="progress-circle__inner"
                />
                <ProgressCirclePart
                    innerWidth={width}
                    outerWidth={width}
                    strokeWidth={strokeWidth}
                    percent={percentProgress}
                    gapWidth={gapWidth}
                    className="progress-circle__bar"
                />
            </svg>
            {children && <span className="progress-circle__content">{children}</span>}
        </div>
    );
};

ProgressCircle.propTypes = {
    className: PropTypes.string,
    gapWidth: PropTypes.number, // To make it a 'partial' circle
    percentProgress: PropTypes.number.isRequired, // 0 - 100
    strokeWidth: PropTypes.number.isRequired,
    width: PropTypes.number.isRequired,
};

ProgressCircle.defaultProps = {
    className: null,
    // The 0.25 adds a 25% gap into the circle (and would need to
    // be adjusted with the rotation in the CSS to keep it aligned)
    gapWidth: 0.25,
};

export default React.memo(ProgressCircle);
